import React, {useState, useContext, useEffect, useRef} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import "./FileUpload.css";
import {Button, Icon, IconButton, ModalContext} from "@spg/component_library";
import API from "../logic/API";
import {DownloadableFile} from "../logic/APITypes";
import closeIcon from "../images/closeIcon.svg";
import copyIcon from "../images/copy.svg";
import loadingSpinner from "../images/loadingSpinner.svg";
import cloudUpload from "../images/cloudUpload.svg";
import reloadArrows from "../images/reloadArrows.svg";
import successIcon from "../images/success.svg";
import {NavigationManagement} from "../views/LandingPage";
import {copyTextToClipboard} from "../logic/clipboard";

type UploadFile = {
  file: File;
  id: string;
  progress: number;
  message?: string;
  status:
    | "queued"
    | "staged"
    | "uploading"
    | "finished"
    | "failed"
    | "prohibited";
};

type SharedUploadProps = {
  /** NavigationManagement to control warnings before navigating away during ongoing upload */
  navigationManagement: NavigationManagement;
  /** Function to call every time a file is uploaded successfully */
  onUploadFinished?: (df: DownloadableFile) => void;
  /** Whether the user can select more than one file to upload */
  allowMultiple: boolean;
  /** Whether to prevent the users from selecting files (or using any buttons) – the element should explain why */
  isDisabled?: JSX.Element;
  /** List of files to initialize the component with for testing */
  unitTestFiles?: UploadFile[];
};

type UserFileUploadProps = SharedUploadProps & {
  /** Function to read and upload files using an XHR to allow for progress events */
  uploadFile: (
    file: File,
    onProgress: (e: ProgressEvent) => void,
    onLoad: (e: ProgressEvent) => void,
    onFail: (e: ProgressEvent) => void,
  ) => {xhr: XMLHttpRequest; reader: FileReader};
};

type TopicFileUploadProps = SharedUploadProps & {
  /** Function to read and upload files using an XHR to allow for progress events */
  uploadFile: (
    file: File,
    onProgress: (e: ProgressEvent) => void,
    onLoad: (e: ProgressEvent) => void,
    onFail: (e: ProgressEvent) => void,
    topicId: string,
    token: string,
  ) => {xhr: XMLHttpRequest; reader: FileReader};
  /** TopicId to upload the file to – will also cause an access token to be fetched before upload */
  uploadToTopicWithId: string;
};
const maxFileSize = 50 * 1000 * 1000;

/**
 * A component allowing users to upload one or multiple files.
 */
function FileUpload(props: UserFileUploadProps | TopicFileUploadProps) {
  const intl = useIntl();
  const modalContext = useContext(ModalContext);

  const [draggingFileOver, setDraggingFileOver] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>(
    process.env.NODE_ENV === "test" && props.unitTestFiles
      ? props.unitTestFiles
      : [],
  );
  const [copyInfo, setCopyInfo] = useState<
    Record<
      string,
      {timeoutId: ReturnType<typeof setTimeout>; state: "#failure" | "#success"}
    >
  >({});
  const currentUpload = useRef<
    {fileId: string} & ReturnType<typeof props.uploadFile>
  >();
  const tokenAbortController = useRef<AbortController>(new AbortController());
  const [dummyInputValue, setDummyInputValue] = useState(""); //allows onInput to be triggered repeatedly even if the same file selection is made
  const [accessToken, setAccessToken] = useState("");

  const uploadButtonVisible =
    uploadFiles.length > 0 &&
    uploadFiles.some(
      (file) => file.status != "finished" && file.status != "prohibited",
    );
  const uploadButtonAsRetry = uploadFiles.every(
    (file) =>
      file.status == "finished" ||
      file.status == "failed" ||
      file.status == "prohibited",
  );
  const cancelButtonVisible =
    uploadButtonVisible &&
    uploadFiles.some(
      (file) => file.status == "staged" || file.status == "uploading",
    );
  const labelText = props.allowMultiple
    ? cancelButtonVisible
      ? "uploading"
      : draggingFileOver
        ? "release"
        : uploadFiles.filter((file) => file.status == "failed").length === 1
          ? "failedOne"
          : uploadFiles.length > 0 &&
              uploadFiles.every((file) => file.status == "failed")
            ? "failedAll"
            : uploadFiles.some((file) => file.status == "failed")
              ? "failedMultiple"
              : uploadFiles.filter((file) => file.status == "finished")
                    .length === 1
                ? "finishedOne"
                : uploadFiles.some((file) => file.status == "finished")
                  ? "finishedMultiple"
                  : uploadFiles.length > 0
                    ? "append"
                    : "default"
    : cancelButtonVisible
      ? "single.uploading"
      : draggingFileOver
        ? "single.release"
        : uploadFiles.length == 0
          ? "single.default"
          : uploadFiles[0].status == "failed"
            ? "single.failed"
            : uploadFiles[0].status == "finished"
              ? "single.finished"
              : "single.replace";

  /** Checks for staged files and calls the upload-Endpoint with callbacks for what to do afterwards. */
  const initializeUpload = () => {
    setUploadFiles((ufOuter) => {
      const nextFileIndex = ufOuter.findIndex(
        (file) => file.status === "staged",
      );
      if (nextFileIndex == -1) return ufOuter;
      if ("uploadToTopicWithId" in props && !accessToken) {
        tokenAbortController.current.abort();
        tokenAbortController.current = new AbortController();
        API.getFileAccessToken(
          props.uploadToTopicWithId,
          tokenAbortController.current,
        ).then((response) => {
          if (response.success && response.payload) {
            setAccessToken(response.payload.token);
          } else {
            setUploadFiles((uf) =>
              uf.map((file) => {
                if (["uploading", "staged", "queued"].includes(file.status)) {
                  return {
                    ...file,
                    status: "failed",
                    message: intl.formatMessage({
                      id: "fileUpload.list.error.token",
                    }),
                  };
                }
                return file;
              }),
            );
            currentUpload.current = undefined;
          }
        });
        return ufOuter;
      }
      const newFiles = [...ufOuter];
      const nextFile = newFiles[nextFileIndex];
      newFiles[nextFileIndex] = {
        ...nextFile,
        status: "uploading",
        message: undefined,
      };
      if (!nextFile) return newFiles;
      if ("uploadToTopicWithId" in props) {
        tokenAbortController.current.abort();
        tokenAbortController.current = new AbortController();
      }
      if (currentUpload.current) {
        currentUpload.current.reader.abort();
        currentUpload.current.xhr.abort();
      }
      const onProgress = (e: ProgressEvent) => {
        setUploadFiles((uf) =>
          uf.map((file) =>
            file.id === nextFile.id
              ? {
                  ...file,
                  progress: Math.round((10000 * e.loaded) / e.total) / 100,
                }
              : file,
          ),
        );
      };
      const onLoad = () => {
        let downloadableFile: DownloadableFile | undefined = undefined;
        try {
          if (currentUpload.current?.xhr.responseText)
            downloadableFile = JSON.parse(
              currentUpload.current.xhr.responseText,
            ) as DownloadableFile;
        } catch (e) {
          // treat unexpected responses as errors (downloadableFile = undefined), e.g. like HTML by nginx
        }
        const updatedStatus =
          currentUpload.current &&
          200 <= currentUpload.current.xhr.status &&
          currentUpload.current.xhr.status < 300
            ? "finished"
            : "failed";
        if (
          updatedStatus == "finished" &&
          downloadableFile &&
          props.onUploadFinished
        )
          props.onUploadFinished(downloadableFile);
        setUploadFiles((uf) => {
          let lookingFor: "current" | "queued" | "end" = "current";
          return uf.map((file) => {
            if (lookingFor == "current" && file.id === nextFile.id) {
              lookingFor = "queued";
              return {
                ...file,
                status: updatedStatus,
                progress: updatedStatus == "finished" ? 100 : 0,
                message:
                  "uploadToTopicWithId" in props
                    ? undefined
                    : updatedStatus == "finished" && downloadableFile
                      ? downloadableFile.url
                      : intl.formatMessage({
                          id:
                            "fileUpload.list.error." +
                            (currentUpload.current?.xhr.status === 413
                              ? "size"
                              : "generic"),
                        }),
              };
            } else if (lookingFor == "queued" && file.status === "queued") {
              lookingFor = "end";
              return {
                ...file,
                status: "staged",
              };
            }
            return file;
          });
        });
        currentUpload.current = undefined;
      };
      const onFail = () => {
        let lookingFor: "current" | "queued" | "end" = "current";
        setUploadFiles((uf) =>
          uf.map((file) => {
            if (lookingFor == "current" && file.id === nextFile.id) {
              lookingFor = "queued";
              return {
                ...file,
                status: "failed",
                message: intl.formatMessage({
                  id: "fileUpload.list.error.generic",
                }),
              };
            } else if (lookingFor == "queued" && file.status === "queued") {
              lookingFor = "end";
              return {
                ...file,
                status: "staged",
              };
            }
            return file;
          }),
        );
        currentUpload.current = undefined;
      };
      currentUpload.current = {
        fileId: nextFile.id,
        ...("uploadToTopicWithId" in props
          ? props.uploadFile(
              nextFile.file,
              onProgress,
              onLoad,
              onFail,
              props.uploadToTopicWithId,
              accessToken,
            )
          : props.uploadFile(nextFile.file, onProgress, onLoad, onFail)),
      };
      return newFiles;
    });
  };

  const addCopyInfo = (fileId: string, success: boolean) => {
    setCopyInfo((ci) => {
      if (ci[fileId]) clearTimeout(ci[fileId].timeoutId);
      const newCopyInfo = {...ci};
      newCopyInfo[fileId] = {
        state: success ? "#success" : "#failure",
        timeoutId: setTimeout(() => removeCopyInfo(fileId), 1000),
      };
      return newCopyInfo;
    });
  };

  const removeCopyInfo = (fileId: string) => {
    setCopyInfo((cu) => {
      const newCopyInfo = {...cu};
      delete newCopyInfo[fileId];
      return newCopyInfo;
    });
  };

  useEffect(
    () => () => {
      for (const fileId in copyInfo) removeCopyInfo(fileId);
    },
    [],
  );

  useEffect(initializeUpload, [uploadFiles, accessToken]);

  useEffect(() => {
    if (props.navigationManagement.isTryingToNavigate.length === 0) return;
    if (!cancelButtonVisible) props.navigationManagement.allowNavigation();
    else
      modalContext.showModal({
        idBase: "fileUpload",
        headline: intl.formatMessage({id: "fileUpload.modal.leave.headline"}),
        body: intl.formatMessage({id: "fileUpload.modal.leave.text"}),
        primaryButton: {
          name: intl.formatMessage({id: "fileUpload.modal.leave.leave"}),
          onClick: props.navigationManagement.allowNavigation,
        },
        secondaryButton: {
          name: intl.formatMessage({id: "fileUpload.modal.leave.cancel"}),
          onClick: props.navigationManagement.preventNavigation,
        },
      });
  }, [props.navigationManagement.isTryingToNavigate]);

  useEffect(
    () => () => {
      if ("uploadToTopicWithId" in props) {
        tokenAbortController.current.abort();
        tokenAbortController.current = new AbortController();
      }
      if (currentUpload.current) {
        currentUpload.current.reader.abort();
        currentUpload.current.xhr.abort();
      }
    },
    [],
  );

  return (
    <div className="landingPageGrid fileUploadContainer">
      {/* File selection input and status indicator */}
      <div
        className={`fileUpload${draggingFileOver ? " draggingFileOver" : ""}${
          props.isDisabled ? " disabled" : ""
        }`}
        onDrop={(e) => {
          if (cancelButtonVisible || props.isDisabled) return;
          setDraggingFileOver(false);
          e.preventDefault();
          let files: UploadFile[] = [];
          const time = Date.now();
          [...Array.from(e.dataTransfer.items)].forEach((item, i) => {
            const file = item.getAsFile();
            if (item.kind === "file" && file)
              files.push({
                file,
                id: `${time}-${i}`,
                progress: 0,
                status: file.size > maxFileSize ? "prohibited" : "queued",
              });
          });
          if (files.length == 0)
            files = Array.from(e.dataTransfer.files).map((file, i) => {
              return {
                file,
                id: `${time}-${i}`,
                progress: 0,
                status: file.size > maxFileSize ? "prohibited" : "queued",
              };
            });
          if (props.allowMultiple || files.length === 0) {
            if (uploadFiles) setUploadFiles((uf) => [...uf, ...files]);
            else setUploadFiles(files);
          } else setUploadFiles([files[0]]);
        }}
        onDragOver={(e) => {
          if (cancelButtonVisible || props.isDisabled) return;
          if (
            [...Array.from(e.dataTransfer.items)].some(
              (item) => item.kind === "file",
            ) ||
            e.dataTransfer.files.length > 0
          )
            setDraggingFileOver(true);
          e.preventDefault();
        }}
        onDragEnd={() => setDraggingFileOver(false)}
        onDragLeave={() => setDraggingFileOver(false)}
        onDragExit={() => setDraggingFileOver(false)}
      >
        <input
          type="file"
          id="fileUploadInput"
          className="sr-only"
          value={dummyInputValue}
          onInput={(e) => {
            setDummyInputValue("");
            const time = Date.now();
            setUploadFiles((uf) => {
              const newFiles = Array.from(
                (e.target as EventTarget & HTMLInputElement).files || [],
              ).map((file, i) => ({
                file,
                id: `${time}-${i}`,
                progress: 0,
                status:
                  file.size > maxFileSize
                    ? "prohibited"
                    : ("queued" as UploadFile["status"]),
              }));
              if (props.allowMultiple || newFiles.length === 0)
                return [...uf, ...newFiles];
              else return [newFiles[0]];
            });
          }}
          multiple={props.allowMultiple}
          disabled={cancelButtonVisible || !!props.isDisabled}
        />
        <label htmlFor="fileUploadInput">
          <Icon
            href={cloudUpload + "#globalG"}
            title={""}
            iconSize={{width: 72, height: 72}}
            strokeColor={draggingFileOver ? "white" : "black"}
          />
          <span>
            <div>
              {props.isDisabled || (
                <FormattedMessage
                  id={"fileUpload.label." + labelText}
                  values={{
                    b: (chunks) => <b>{chunks}</b>,
                    br: () => <br />,
                    count: uploadFiles.filter(
                      (file) => file.status == "finished",
                    ).length,
                  }}
                />
              )}
            </div>
          </span>
          {(labelText == "default" ||
            labelText == "append" ||
            labelText == "single.default") && (
            <span className="fileUploadLabelInfo">
              <FormattedMessage id="fileUpload.label.size" />
            </span>
          )}
        </label>
      </div>
      {/* List of files to upload */}
      {uploadFiles.length > 0 && (
        <>
          <ul id="fileQueue" className="fileUploadFiles">
            {uploadFiles.map((file, i) => (
              <li key={file.id} className={file.status}>
                <span>{file.file.name}</span>
                {file.status == "finished" ? (
                  <a href={file.message} target="_blank" rel="noreferrer">
                    {file.message}
                  </a>
                ) : file.status == "prohibited" ? (
                  <span>
                    <FormattedMessage id="fileUpload.list.error.size" />
                  </span>
                ) : (
                  <span>{file.message}</span>
                )}
                <span className="fileUploadFilesButtonContainer">
                  {file.status == "failed" && (
                    <IconButton
                      idBase={"fileUploadFilesRetry" + file.id}
                      onClick={() =>
                        setUploadFiles((uf) =>
                          uf.map((f) =>
                            f.id === file.id
                              ? {
                                  ...file,
                                  status: uf.some(
                                    (f2) =>
                                      f2.status == "uploading" ||
                                      f2.status == "staged",
                                  )
                                    ? "queued"
                                    : "staged",
                                }
                              : f,
                          ),
                        )
                      }
                      disabled={!!props.isDisabled}
                      iconProps={{
                        href: reloadArrows + "#globalG",
                        strokeColor: "black",
                        title: intl.formatMessage({
                          id: "fileUpload.list.retry",
                        }),
                      }}
                    />
                  )}
                  {(file.status == "queued" && cancelButtonVisible) ||
                  file.status == "uploading" ||
                  file.status == "staged" ? (
                    <img
                      src={loadingSpinner}
                      alt={intl.formatMessage({
                        id: "fileUpload.list.loading",
                      })}
                      width={24}
                      height={24}
                    />
                  ) : file.status == "finished" &&
                    "uploadToTopicWithId" in props ? (
                    <img
                      src={successIcon}
                      alt={intl.formatMessage({
                        id: "fileUpload.list.finished",
                      })}
                      width={24}
                      height={24}
                    />
                  ) : file.status == "finished" ? (
                    <IconButton
                      idBase={"fileUploadFilesCopy" + file.id}
                      onClick={() => {
                        if (!file.message) return;
                        copyTextToClipboard(file.message).then(
                          () => addCopyInfo(file.id, true),
                          () => addCopyInfo(file.id, false),
                        );
                      }}
                      disabled={!!props.isDisabled}
                      iconProps={{
                        href:
                          copyIcon +
                          (copyInfo[file.id]
                            ? copyInfo[file.id].state
                            : "#globalG"),
                        strokeColor: "black",
                        title: intl.formatMessage({id: "fileUpload.list.copy"}),
                      }}
                    />
                  ) : (
                    <IconButton
                      idBase={"fileUploadFilesRemove" + file.id}
                      onClick={() => {
                        const newFiles = [...uploadFiles];
                        newFiles.splice(i, 1);
                        setUploadFiles(newFiles);
                      }}
                      disabled={!!props.isDisabled}
                      iconProps={{
                        href: closeIcon + "#globalG",
                        strokeColor: "black",
                        title: intl.formatMessage({
                          id: "fileUpload.list.remove",
                        }),
                      }}
                    />
                  )}
                </span>
                {((file.status == "queued" && cancelButtonVisible) ||
                  file.status == "staged" ||
                  file.status == "uploading" ||
                  file.status == "finished") && (
                  <progress
                    max={100}
                    value={file.progress}
                    aria-label={intl.formatMessage(
                      {
                        id: "fileUpload.list.progress",
                      },
                      {fileName: file.file.name},
                    )}
                  >
                    {file.progress + "%"}
                  </progress>
                )}
              </li>
            ))}
          </ul>
          {uploadButtonVisible && (
            <div className="fileUploadButtons">
              {cancelButtonVisible && (
                <Button
                  label={intl.formatMessage({id: "fileUpload.button.cancel"})}
                  idBase={"fileUploadButtonsCancel"}
                  onClick={() => {
                    if ("uploadToTopicWithId" in props) {
                      tokenAbortController.current.abort();
                      tokenAbortController.current = new AbortController();
                    }
                    if (currentUpload.current) {
                      currentUpload.current.reader.abort();
                      currentUpload.current.xhr.abort();
                      currentUpload.current = undefined;
                    }
                    setUploadFiles((uf) =>
                      uf.map((file) =>
                        file.status === "finished" ||
                        file.status === "prohibited"
                          ? file
                          : {
                              ...file,
                              status: "failed",
                              progress: 0,
                              message: intl.formatMessage({
                                id: "fileUpload.list.cancel",
                              }),
                            },
                      ),
                    );
                  }}
                  isDisabled={!!props.isDisabled}
                  configuration={"tertiary"}
                  isWide
                />
              )}
              <Button
                label={
                  uploadButtonAsRetry
                    ? intl.formatMessage(
                        {
                          id: `fileUpload.button.${
                            props.allowMultiple ? "" : "single."
                          }retry`,
                        },
                        {
                          count: uploadFiles.filter(
                            (file) => file.status == "failed",
                          ).length,
                        },
                      )
                    : intl.formatMessage({id: "fileUpload.button.upload"})
                }
                idBase={"fileUploadButtonsUpload"}
                onClick={() => {
                  setUploadFiles((uf) => {
                    const newFiles: UploadFile[] = uploadButtonAsRetry
                      ? uf.map((file) =>
                          file.status == "failed"
                            ? {...file, status: "queued"}
                            : file,
                        )
                      : [...uf];
                    const firstQueuedIndex = newFiles.findIndex(
                      (file) => file.status === "queued",
                    );
                    newFiles[firstQueuedIndex] = {
                      ...newFiles[firstQueuedIndex],
                      status: "staged",
                    };
                    return newFiles;
                  });
                }}
                configuration={"primary"}
                isWide
                isDisabled={cancelButtonVisible || !!props.isDisabled}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export {FileUpload, type UploadFile};
